import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from 'next-themes';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { User, Moon, Sun, Bell, Download, LogOut, Mail, Save } from 'lucide-react';

export default function SettingsPage() {
  const { user, updateUser, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [name, setName] = useState(user?.name || '');
  const [email] = useState(user?.email || '');
  const [goals, setGoals] = useState(user?.goals || '');
  const [notifications, setNotifications] = useState(user?.notifications !== false);
  const [isAthlete, setIsAthlete] = useState(user?.is_athlete || false);
  const [sport, setSport] = useState(user?.sport || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/auth/me', { name, goals, notifications, theme, is_athlete: isAthlete, sport });
      updateUser(res.data);
      toast.success('Configurações salvas!');
    } catch (err) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `virtu-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Dados exportados com sucesso!');
    } catch (err) { toast.error('Erro ao exportar'); }
  };

  const handleLogout = () => {
    logout();
    toast.success('Até a próxima! Continue forte.');
  };

  const isDark = theme === 'dark';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground text-sm mt-1">Personalize sua experiência</p>
      </div>

      {/* Profile */}
      <Card data-testid="profile-settings">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <User className="w-5 h-5" /> Perfil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold text-2xl font-heading">
              {name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-medium">{name}</p>
              <p className="text-sm text-muted-foreground">{email}</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="settings-name">Nome</Label>
            <Input id="settings-name" value={name} onChange={e => setName(e.target.value)} className="rounded-xl h-11" data-testid="settings-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={email} disabled className="rounded-xl h-11 opacity-60" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="settings-goals">Metas pessoais</Label>
            <Input id="settings-goals" placeholder="Ex: Ficar 90 dias consistente, meditar todo dia..." value={goals} onChange={e => setGoals(e.target.value)} className="rounded-xl h-11" data-testid="settings-goals-input" />
          </div>
          <div className="space-y-2">
            <Label>Perfil de atleta</Label>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Ative se você quiser acompanhar métricas específicas de esporte
              </p>
              <Switch
                checked={isAthlete}
                onCheckedChange={setIsAthlete}
                data-testid="settings-athlete-toggle"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Esporte principal</Label>
            <select
              disabled={!isAthlete}
              value={sport}
              onChange={e => setSport(e.target.value)}
              className="w-full rounded-xl border bg-background h-11 px-3 text-sm"
              data-testid="settings-sport-select"
            >
              <option value="">Selecione...</option>
              <option value="futebol">Futebol</option>
              <option value="corrida">Corrida</option>
              <option value="musculacao">Musculação</option>
              <option value="ciclismo">Ciclismo</option>
              <option value="crossfit">Crossfit</option>
              <option value="natacao">Natação</option>
              <option value="outro">Outro</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card data-testid="preferences-settings">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Preferências</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isDark ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-yellow-500" />}
              <div>
                <p className="text-sm font-medium">Modo escuro</p>
                <p className="text-xs text-muted-foreground">Alterne entre claro e escuro</p>
              </div>
            </div>
            <Switch
              checked={isDark}
              onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              data-testid="theme-toggle"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-medium">Notificações</p>
                <p className="text-xs text-muted-foreground">Lembretes e alertas</p>
              </div>
            </div>
            <Switch
              checked={notifications}
              onCheckedChange={setNotifications}
              data-testid="notifications-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card data-testid="actions-settings">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Ações</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={handleSave} className="w-full rounded-xl h-11" disabled={saving} data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </Button>
          <Button variant="outline" onClick={handleExport} className="w-full rounded-xl h-11" data-testid="export-data-btn">
            <Download className="w-4 h-4 mr-2" /> Exportar dados (JSON)
          </Button>
          <Separator />
          <Button variant="outline" onClick={handleLogout} className="w-full rounded-xl h-11 text-destructive-foreground border-destructive/30 hover:bg-destructive/10" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sair da conta
          </Button>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground space-y-1 pb-8">
        <div className="flex items-center justify-center gap-1">
          <Mail className="w-3 h-3" />
          <span>suporte@appvirtu.com.br</span>
        </div>
        <p>&copy; 2026 VIRTU - Todos os direitos reservados</p>
      </div>
    </div>
  );
}
